import { FeatureCard, Button, Colors } from '@rockalingua/ui';
import { TeamOutlined, UserOutlined } from '@ant-design/icons';

import type { OrganizationalLicense } from '@/v2/domains/Subscriptions/types/subscription';
import getOrganizationalLicensePayer from '@/v2/domains/Subscriptions/utils/getOrganizationalLicensePayer';
import { useExpiredSubscriptionModalStore } from '@/v2/domains/Subscriptions/stores/useExpiredSubscriptionModal.store';
import useManageSubscriptionActions from '@/v2/domains/Subscriptions/hooks/useManageSubscriptionActions.hook';

import ExpiredSubscriptionContainer from '../ExpiredSubscriptionBannerContainer/ExpiredSubscriptionContainer';
import {
  StepsSection,
  Actions,
} from './OrganizationExpiredSubscriptionBanner.styled';

type OrganizationExpiredSubscriptionBannerProps = {
  organizationalLicense: OrganizationalLicense;
  title?: string;
  size?: 'large' | 'small';
};

export default function OrganizationExpiredSubscriptionBanner({
  organizationalLicense,
  title,
  size = 'large',
}: OrganizationExpiredSubscriptionBannerProps) {
  const { setModalClosed } = useExpiredSubscriptionModalStore();
  const { showPlans } = useManageSubscriptionActions();
  const payer = getOrganizationalLicensePayer(organizationalLicense);

  const handleRenew = () => {
    setModalClosed(true);
    showPlans();
  };

  const steps = [
    {
      title: `Ask your ${payer} to contact us`,
      subtitle: "We'll help them renew your license.",
      iconBgColor: Colors.MAIN_VARIANTS[30],
      icon: <TeamOutlined style={{ color: Colors.MAIN_VARIANTS[70] }} />,
    },
    {
      title: 'Prefer to pay yourself?',
      subtitle: 'Choose a plan and subscribe on your own.',
      iconBgColor: Colors.ACCENT_VARIANTS[30],
      icon: <UserOutlined style={{ color: Colors.ACCENT_VARIANTS[90] }} />,
    },
  ];

  return (
    <ExpiredSubscriptionContainer
      title={title || 'Your subscription has expired'}
      subtitle="Don't worry, your classes and students are saved."
      size={size}
    >
      <StepsSection>
        {steps.map((step) => (
          <FeatureCard
            key={step.title}
            title={step.title}
            subtitle={step.subtitle}
            iconBgColor={step.iconBgColor}
            icon={step.icon}
          />
        ))}
      </StepsSection>

      <Actions>
        {size == 'large' && (
          <Button type="link" size="large" onClick={() => setModalClosed(true)}>
            Leave for now
          </Button>
        )}
        <Button type="primary" size={size} onClick={handleRenew}>
          Renew on my own
        </Button>
      </Actions>
    </ExpiredSubscriptionContainer>
  );
}
